import { Box, Button, IconButton, Paper, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineEye } from "react-icons/ai";
import { FiEdit, FiFileText } from "react-icons/fi";
import { IoIosRemoveCircleOutline, IoMdCheckmarkCircleOutline } from "react-icons/io";
import { LuBookMarked, LuChartColumn, LuDatabase, LuFactory, LuFlaskConical, LuFolderOpen, LuImage, LuLeaf, LuLock, LuLockOpen, LuNewspaper, LuQrCode, LuRecycle, LuShield, LuWrench } from "react-icons/lu";
import { MdOutlineRadioButtonUnchecked } from "react-icons/md";
import { RiDownloadCloud2Line, RiFileUploadLine } from "react-icons/ri";
import { useParams } from "react-router-dom";
import { fetchWithToken, useAuth } from "../../authentication/AuthProvider";
import { MainContainer } from "../../components/container/MainContainer";
import { PageTitleContainer } from "../../components/container/PageTitleContainer";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import CheckBox from "../../components/input/CheckBox";
import ImageUpload from "../../components/input/ImageUpload";
import TextBox from "../../components/input/TextBox";
import config from "../../config";
import { DPP } from "../../db_access/dpp";
import { Product } from "../../db_access/product";
import { dpp_format } from "../Products/DPP/ProductDPP_Format";
import ProductDPP_Preview from "../Products/DPP/ProductDPP_Preview";
const DPP_Edit_Pending = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { dpp_id } = useParams();
  const fileInputRef = useRef(null);
  const [modified, setModified] = useState(false);
  const [editMode, setEditMode] = useState(true);
  const [dpp, setDPP] = useState(
    dpp_format.reduce((acc, item) => {
      acc[item.field] = item.default;
      return acc;
    }, {})
  );
  const [sectionEditability, setSectionEditability] = useState(
    [...new Set(dpp_format.map((item) => item.section))].reduce((acc, section) => {
      acc[section] = true;
      return acc;
    }, {})
  );
  const isFieldEditable = (field) => {
    const format = dpp_format.find((f) => f.field === field);
    return format ? sectionEditability[format.section] ?? true : true;
  };
  const [photo, setPhoto] = useState();
  const [photoPreview, setPhotoPreview] = useState();
  const isCAdmin = user["is_company_admin"] === 1;

  const buttonCustomize = (label, Icon, onClick) => (
    <Button
      onClick={onClick}
      sx={{
        color: "#166534",
        textTransform: "none",
        "&:hover": { backgroundColor: "rgba(23, 163, 74, 0.2)" },
        fontFamily: "Poppins",
      }}
      startIcon={<Icon />}>
      {label}
    </Button>
  );
  const toPreviewMode = () => setEditMode(false);
  const buttonPreviewMode = () => buttonCustomize("Preview", AiOutlineEye, toPreviewMode);
  const toEditMode = () => setEditMode(true);
  const buttonEditMode = () => buttonCustomize("Edit", FiEdit, toEditMode);
  const savePhoto = (dpp_id) => {
    const fd = new FormData();
    fd.append("id", dpp_id);
    fd.append("photo", photo);
    DPP.save_photo(fd);
  };
  const publish = () => {
    DPP.publish(dpp["id"], dpp["_id"]);
  };
  const buttonPublish = () => buttonCustomize("Publish", LuNewspaper, publish);
  const updateChanges = () => {
    DPP.update(dpp)
      .then((res) => res.json())
      .then((_) => setModified(false));
    savePhoto(dpp_id);
  };
  const buttonUpdateChanges = () => buttonCustomize("Update Changes", LuNewspaper, updateChanges);
  const loadPhotoFromArivu = () => {
    Product.get_photo(dpp["id"])
      .then((data) => setPhoto(data))
      .finally(() => setModified(true));
  };
  const loadFromArivu = () => {
    Product.get_by_id(dpp["id"])
      .then((res) => res.json())
      .then((data) =>
        setDPP((prev) => {
          const product = data[0];
          const passport = {
            mci: product.mci,
            gwp_total: product.gwp_total,
            manufacturer_name: product.supplier,
            manufacturer_address: product.detail_address,
            manufacturer_email: product.detail_email,
            manufacturer_phone: product.detail_phone,
            manufacturer_contact_person: product.detail_contact_person,
            service_life: product.service_life,
            extended_service_life: product.extended_service_life,
            leed_category: product.leed_category,
            sub_category: product.sub_category,
            uniformat_II_1: product.uniformat_II_1,
            uniformat_II_2: product.uniformat_II_2,
            place_of_manufacture: product.place_of_manufacture,
            place_of_use: product.place_of_use,
            product_name: product.product,
          };
          const updated = {};
          const appended = {};

          for (const key in passport) {
            if (isFieldEditable(key)) {
              updated[key] = passport[key];
            } else {
              const format = dpp_format.find((f) => f.field === key);
              if (format && format.type === "list") {
                appended[key] = passport[key];
              }
            }
          }
          for (const key in appended) {
            const prevList = Array.isArray(prev[key]) ? prev[key] : [];
            updated[key] = [...prevList, ...appended[key]];
          }
          return {
            ...prev,
            ...updated,
          };
        })
      )
      .finally(() => setModified(true));
    loadPhotoFromArivu();
  };
  const buttonLoadFromArivu = () => buttonCustomize("Load from Arivu", RiDownloadCloud2Line, loadFromArivu);

  const loadFromPDF = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const buttonLoadFromPDF = () => buttonCustomize("Load from PDF", RiFileUploadLine, loadFromPDF);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file || file.type !== "application/pdf") {
      return;
    }
    const url = `${config.apiUrl}/api/dpp/pdf_to_passport_json_endpoint`;
    const body = new FormData();
    body.append("file", file);
    fetchWithToken(url, {
      ...config.method.POST,
      body,
    })
      .then((res) => res.json())
      .then((data) => {
        const passport = data["data"];
        const materials_json = passport["materials_json"];
        const materials = typeof materials_json === "string" ? JSON.parse(materials_json) : materials_json;
        passport["materials"] = materials;
        const certifications_json = passport["certifications_json"];
        const certifications = typeof certifications_json === "string" ? JSON.parse(certifications_json) : certifications_json;
        passport["certifications"] = certifications;
        setDPP((prev) => {
          const updated = {};
          const appended = {};

          for (const key in passport) {
            if (isFieldEditable(key)) {
              updated[key] = passport[key];
            } else {
              const format = dpp_format.find((f) => f.field === key);
              if (format && format.type === "list") {
                appended[key] = passport[key];
              }
            }
          }
          for (const key in appended) {
            const prevList = Array.isArray(prev[key]) ? prev[key] : [];
            updated[key] = [...prevList, ...appended[key]];
          }
          return {
            ...prev,
            ...updated,
          };
        });
      })
      .finally(() => setModified(true));
  };
  const onChange = (e, field, index = undefined, subField = undefined) => {
    const attribute = dpp_format.find((d) => d.field === field);
    setModified(true);
    if (["string"].includes(attribute.type)) {
      setDPP((p) => ({ ...p, [field]: e.target.value }));
    } else if (["float"].includes(attribute.type)) {
      setDPP((p) => ({ ...p, [field]: parseFloat(e.target.value) }));
    } else if (["boolean"].includes(attribute.type)) {
      setDPP((p) => ({ ...p, [field]: e.target.checked }));
    } else if (["list"].includes(attribute.type)) {
      const subAttribute = attribute.itemDef.find((d) => d.field === subField);
      setDPP((p) => ({
        ...p,
        [field]: p[field].map((item, i) => {
          if (i === index) {
            if (["string", "float"].includes(subAttribute.type)) {
              return { ...item, [subField]: e.target.value };
            } else if (["boolean"].includes(subAttribute.type)) {
              return { ...item, [subField]: e.target.checked };
            }
          }
          return item;
        }),
      }));
    }
  };
  const buttonLock = (visible, onClick) => {
    return (
      <Box sx={{ display: visible ? "block" : "none" }}>
        <IconButton
          onClick={onClick}
          sx={{
            "&:hover": { backgroundColor: "transparent" },
            padding: 1,
            color: "black",
          }}>
          <LuLock />
        </IconButton>
      </Box>
    );
  };
  const buttonUnlock = (visible, onClick) => {
    return (
      <Box sx={{ display: visible ? "block" : "none" }}>
        <IconButton
          onClick={onClick}
          sx={{
            "&:hover": { backgroundColor: "transparent", color: "black" },
            padding: 1,
            color: "lightgray",
          }}>
          <LuLockOpen />
        </IconButton>
      </Box>
    );
  };
  const buttonSectionLockUnlock = (section) => {
    const editable = sectionEditability[section] ?? true;
    const setEditable = (value) => {
      setSectionEditability((prev) => ({ ...prev, [section]: value }));
    };
    return (
      <Box>
        {buttonLock(!editable, () => setEditable(true))}
        {buttonUnlock(editable, () => setEditable(false))}
      </Box>
    );
  };
  const dpp_control = (section) => {
    const editable = sectionEditability[section] ?? true;
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {dpp_format
          .filter((d) => d.section === section)
          .map((d, i) => {
            if (["string", "float"].includes(d.type)) {
              return (
                <Box
                  key={i}
                  sx={{
                    minWidth: "300px",
                    display: "flex",
                    alignItems: "flex-end",
                  }}>
                  <TextBox label={d.label} value={dpp[d.field]} onChange={(e) => onChange(e, d.field)} disabled={d.disabled || !editable} />
                </Box>
              );
            }
            if (["boolean"].includes(d.type)) {
              return (
                <Box
                  key={i}
                  sx={{
                    minWidth: "300px",
                    display: "flex",
                    alignItems: "flex-end",
                  }}>
                  <CheckBox
                    label={d.label}
                    checked={dpp[d.field]}
                    onChange={(e) => onChange(e, d.field)}
                    Icon={MdOutlineRadioButtonUnchecked}
                    CheckIcon={IoMdCheckmarkCircleOutline}
                    disabled={!editable}
                  />
                </Box>
              );
            }
            if (["list"].includes(d.type)) {
              const newItem = () => {
                const defaultItem = d.itemDef.reduce((acc, def) => {
                  acc[def.field] = def.default;
                  return acc;
                }, {});
                setDPP((p) => ({
                  ...p,
                  [d.field]: [...p[d.field], defaultItem],
                }));
              };
              const itemDef = d.itemDef;
              return (
                <Box key={i} sx={{ width: 1 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {dpp[d.field].map((m, mi) => (
                      <Box
                        key={mi}
                        sx={{
                          display: "flex",
                        }}>
                        <IconButton
                          disabled={!editable}
                          sx={{
                            marginTop: 2.5,
                            marginBottom: "auto",
                            color: "red",
                          }}
                          onClick={() => {
                            setDPP((p) => ({
                              ...p,
                              [d.field]: p[d.field].filter((item, index) => index !== mi),
                            }));
                          }}>
                          <IoIosRemoveCircleOutline />
                        </IconButton>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                          }}>
                          {itemDef.map((def, defindex) => {
                            if (["string", "float"].includes(def.type)) {
                              return (
                                <Box key={defindex} sx={{ minWidth: "260px" }}>
                                  <TextBox label={def.label} value={m[def.field]} onChange={(e) => onChange(e, d.field, mi, def.field)} disabled={!editable} />
                                </Box>
                              );
                            }
                            if (["boolean"].includes(def.type)) {
                              return (
                                <Box
                                  key={defindex}
                                  sx={{
                                    minWidth: "300px",
                                    alignItems: "flex-end",
                                    display: "flex",
                                  }}>
                                  <CheckBox
                                    label={def.label}
                                    checked={m[def.field]}
                                    onChange={(e) => onChange(e, d.field, mi, def.field)}
                                    Icon={MdOutlineRadioButtonUnchecked}
                                    CheckIcon={IoMdCheckmarkCircleOutline}
                                    disabled={!editable}
                                  />
                                </Box>
                              );
                            }
                            return null;
                          })}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ display: "flex", opacity: editable ? 1 : 0.5, pointerEvents: editable ? "auto" : "none" }}>
                    <Button onClick={newItem}>More {d.label}</Button>
                  </Box>
                </Box>
              );
            }
            return null;
          })}
      </Box>
    );
  };
  const section_title = (title, Icon, iconColor) => {
    return (
      <TableHeaderContainer>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            padding: 1,
          }}>
          <Icon color={iconColor} size={20} />
          <Typography
            sx={{
              color: "black",
              fontFamily: "Poppins",
              fontSize: "22px",
              fontWeight: 500,
            }}>
            {title}
          </Typography>
        </Box>
        <Box>{buttonSectionLockUnlock(title)}</Box>
      </TableHeaderContainer>
    );
  };
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
    } else {
      setPhoto(undefined);
    }
  };
  const getPassport = () => {
    DPP.get(dpp_id)
      .then((res) => res.json())
      .then((data) => {
        setDPP({ ...dpp, ...data[0] });
      })
      .finally(() => setModified(false));
  };
  const getPhoto = () => {
    DPP.get_photo(dpp_id)
      .then((res) => (res.ok ? res.blob() : undefined))
      .then((f) => setPhoto(f));
  };
  useEffect(() => console.log(dpp), [dpp]);
  useEffect(() => console.log(sectionEditability), [sectionEditability]);
  useEffect(() => {
    if (dpp_id) {
      getPassport();
      getPhoto();
    }
  }, [dpp_id]);
  useEffect(() => {
    if (photo) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(photo);
    } else {
      setPhotoPreview(undefined);
    }
  }, [photo]);
  useEffect(() => console.log(photo), [photo]);
  return (
    <MainContainer>
      <PageTitleContainer>
        <Box sx={{ padding: 1 }}>
          <Box>
            <Typography variant="Page Title">Digital Product Passport - Edit Pending</Typography>
          </Box>
          <Box>
            <Typography variant="Page Subtitle">EU Regulation Compliant DPP</Typography>
          </Box>
        </Box>
        <Box sx={{ display: modified || !isCAdmin ? "none" : "block" }}>{buttonPublish()}</Box>
        <Box sx={{ display: modified ? "block" : "none" }}>{buttonUpdateChanges()}</Box>
      </PageTitleContainer>
      <Box sx={{ display: "flex" }}>
        <Box sx={{ display: editMode ? "block" : "none" }}>{buttonPreviewMode()}</Box>
        <Box sx={{ display: editMode ? "none" : "block" }}>{buttonEditMode()}</Box>
        {buttonLoadFromArivu()}
        {buttonLoadFromPDF()}
      </Box>
      <Box sx={{ display: editMode ? "flex" : "none", gap: 2, paddingY: 2 }}>
        <Box sx={{ flex: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <Paper sx={{ padding: 2 }}>
            {section_title("Product Identity", FiFileText, "#2cbea5")}
            {dpp_control("Product Identity")}
          </Paper>
          <Paper sx={{ padding: 2 }}>
            {section_title("Environmental Impact", LuLeaf, "#2cbea5")}
            {dpp_control("Environmental Impact")}
          </Paper>
          <Paper sx={{ padding: 2 }}>
            {section_title("Material Composition", LuFactory, "#2cbea5")}
            {dpp_control("Material Composition")}
          </Paper>
          <Paper sx={{ padding: 2 }}>
            {section_title("Certificates", FiFileText, "#2cbea5")}
            {dpp_control("Certificates")}
          </Paper>
          <Paper sx={{ padding: 2 }}>
            {section_title("Durability, Repair & Circular Design", LuWrench, "#2cbea5")}
            {dpp_control("Durability, Repair & Circular Design")}
          </Paper>
          <Paper sx={{ padding: 2 }}>
            {section_title("Functional Performance & Design", LuChartColumn, "#2cbea5")}
            {dpp_control("Functional Performance & Design")}
          </Paper>
          <Paper sx={{ padding: 2 }}>
            {section_title("Substances of Concern", LuFlaskConical, "#2cbea5")}
            {dpp_control("Substances of Concern")}
          </Paper>
          <Paper sx={{ padding: 2 }}>
            {section_title("Producer & Facility Identification", LuFolderOpen, "#2cbea5")}
            {dpp_control("Producer & Facility Identification")}
          </Paper>
          <Paper sx={{ padding: 2 }}>
            {section_title("Compliance & Other Union Law", LuBookMarked, "#2cbea5")}
            {dpp_control("Compliance & Other Union Law")}
          </Paper>
          <Paper sx={{ padding: 2 }}>
            {section_title("DPP System — Access, Governance & Registry", LuDatabase, "#2cbea5")}
            {dpp_control("DPP System — Access, Governance & Registry")}
          </Paper>
          <Paper sx={{ padding: 2 }}>
            {section_title("Data Carrier", LuQrCode, "#2cbea5")}
            {dpp_control("Data Carrier")}
          </Paper>
        </Box>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Paper sx={{ padding: 2 }}>
            <TableHeaderContainer>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  padding: 1,
                }}>
                <LuImage color={"#2cbea5"} size={20} />
                <Typography
                  sx={{
                    color: "black",
                    fontFamily: "Poppins",
                    fontSize: "22px",
                    fontWeight: 500,
                  }}>
                  Photo
                </Typography>
              </Box>
            </TableHeaderContainer>
            <ImageUpload placeholder={"Click to upload photo"} onChange={handlePhotoChange} preview={photoPreview} accept={"image/*"} deletePreview={() => setPhoto(undefined)} />
          </Paper>
          <Paper sx={{ padding: 2 }}>
            {section_title(t("Compliance"), LuShield, "#2cbea5")}
            {dpp_control(t("Compliance"))}
          </Paper>
          <Paper sx={{ padding: 2 }}>
            {section_title("Circularity", LuRecycle, "#2cbea5")}
            {dpp_control("Circularity")}
          </Paper>
          <Box>
            <input ref={fileInputRef} type="file" accept="application/pdf" onChange={handleFileChange} style={{ display: "none" }} />
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: editMode ? "none" : "block", gap: 2, paddingY: 2 }}>
        <ProductDPP_Preview dpp={dpp} photoPreview={photoPreview} />
      </Box>
    </MainContainer>
  );
};
export default DPP_Edit_Pending;
